import FilterAvatar from '@containers/explorer/components/FilterAvatar'
import { TraitType, TraitTypes } from '@models/Attributes'
import { NftData } from '@models/Nft'
import { Avatar, Box } from '@mui/material'
import { ExplorerAttribute } from 'types/explorer'

export const DefaultFilterOptions: ExplorerAttribute = {
  background: '',
  body: '',
  eyes: '',
  nose: '',
  eyewear: '',
  clothes: '',
  ears: '',
  whiskers: '',
  mouth: '',
  accessory: '',
  'head-accessory': '',
  'floating-item': '',
  headgear: '',
  aura: '',
  auraChecked: false,
  staked: false,
  listed: false,
}

const getTraitName = (value: string, trait: TraitType) => {
  if (typeof value !== 'string') return ''
  const filterWord = value
    .split(trait)
    .filter((word) => word !== trait)
    .join('')
    .split('-')
    .filter((word) => word.trim().length !== 0 && !word.includes('animated'))
  const capitalize = filterWord.map(
    (value) =>
      value.at(0)?.toUpperCase() +
      value.substring(1, value.length).toLowerCase(),
  )
  return capitalize.join(' ')
}

export const getName = (value: string, trait?: TraitType) => {
  if (trait) {
    return getTraitName(value, trait)
  }
  const split = value.split('-')
  const capitalize = split.map(
    (value) =>
      value.at(0)?.toUpperCase() +
      value.substring(1, value.length).toLowerCase(),
  )
  return capitalize.join(' ')
}

export const isValidAttribute = (key: string, value: string | boolean) => {
  return (
    Object.keys(DefaultFilterOptions).includes(key as TraitType) &&
    value !== 'NONE' &&
    value !== ''
  )
}

export const isTraitType = (value: string): value is TraitType =>
  Object.keys(DefaultFilterOptions).includes(value)

export const filterNFTs = (
  nfts: NftData[],
  criteria: ExplorerAttribute,
  searchString: string,
) => {
  const filterCriteria = Object.entries(criteria).reduce(
    (acc, [key, value]) => {
      // Remove undefined, empty strings, 'listed', 'unminted', 'auraChecked'
      if (
        value !== undefined &&
        value !== '' &&
        key !== 'listed' &&
        key !== 'staked' &&
        key !== 'auraChecked'
      ) {
        acc[key] = value
      }
      return acc
    },
    {} as any,
  )

  return nfts.filter((nft) => {
    const nameMatch = searchString
      ? nft.name.toLowerCase().includes(searchString.toLowerCase())
      : true

    const listedMatch = criteria.listed ? nft.listed : true
    const stakedMatch = criteria.staked ? nft.staked : true
    const legendaryMatch = criteria.auraChecked ? !!nft.attribute.aura : true

    const attributeMatches = matchesCriteria(nft.attribute, filterCriteria)

    return (
      listedMatch &&
      stakedMatch &&
      nameMatch &&
      legendaryMatch &&
      attributeMatches
    )
  })
}

export const matchesCriteria = (object: any, criteria: any) => {
  for (const [key, value] of Object.entries(criteria)) {
    if (object[key] !== value) {
      return false
    }
  }
  return true
}
export const allBodyTypes =
  /\b(?:demon-slayer|devil-kitty|egyptian|ghost|gold|grumpy|milkyway|normal|ragdoll|robo|sage|sapphire|savanna|scottish-fold|skeleton-kitty|sphynx|tyedye|white-tiger)\b/
export const subLayerBodyTypes =
  /\b(?:ghost|gold|milkyway|sapphire|tyedye|skeleton-kitty)\b/

export type SubCategory =
  | 'demon-slayer'
  | 'devil-kitty'
  | 'egyptian'
  | 'ghost'
  | 'gold'
  | 'grumpy'
  | 'milkyway'
  | 'normal'
  | 'ragdoll'
  | 'robo'
  | 'sage'
  | 'sapphire'
  | 'savanna'
  | 'scottish-fold'
  | 'skeleton-kitty'
  | 'sphynx'
  | 'tyedye'
  | 'white-tiger'
  | 'other'
  | 'normal'

export const getSubCategoryList = (nfts: NftData[], selectedTrait: string) => {
  const subCategoryList = new Map<SubCategory, boolean>()

  nfts.forEach((nft) => {
    const name = nft.name

    switch (selectedTrait) {
      case 'ears':
      case 'body':
        if (!name.match(subLayerBodyTypes)) {
          subCategoryList.set('other', true)
          return
        }
        break

      case 'accessory':
        if (name.includes('skeleton-kitty')) return
        if (name === 'tail-accessory') {
          subCategoryList.set('devil-kitty', true)
          return
        }
        if (
          name === 'gold-chain-accessory' ||
          name === 'gold-chain-outlined-accessory'
        ) {
          subCategoryList.set('normal', true)
          return
        }
        break

      case 'clothes':
        if (name === 'demon-slayer-uniform-clothes') {
          subCategoryList.set('normal', true)
          return
        }
        if (name.includes('skeleton-kitty')) return
        break
      case 'headgear':
      case 'head-accessory':
        if (name.includes('skeleton-kitty')) return
        break
    }

    const match = name.match(allBodyTypes)
    if (match) {
      subCategoryList.set(match[0] as SubCategory, true)
    } else {
      subCategoryList.set('normal', true)
    }
  })

  const subs = Array.from(subCategoryList.keys()) as SubCategory[]
  return subs
}

export const getFilteredAttributes = (
  nfts: NftData[],
  subCategoryList: SubCategory[],
  selectedSub: SubCategory | '',
  selectedTrait: TraitType,
) => {
  if (!nfts) return []
  if (subCategoryList.length === 0 || !selectedSub) return nfts

  const filteredNfts = nfts.filter((nft) => {
    const name = nft.name

    switch (selectedSub) {
      case 'devil-kitty':
        if (selectedTrait === 'accessory') return name === 'tail-accessory'
        break
      case 'grumpy':
        if (selectedTrait === 'mouth') return name === 'grumpy-mouth'
        break
      case 'sage':
        return name === 'sage-eyes'
      case 'other':
        return !name.match(subLayerBodyTypes)
      case 'normal':
        switch (selectedTrait) {
          case 'accessory':
            if (name === 'tail-accessory') return false
            if (
              name === 'gold-necklace-accessory' ||
              name === 'gold-chain-outlined-accessory'
            )
              return true
            break

          case 'clothes':
            if (name === 'demon-slayer-uniform-clothes') return true
            break
          case 'eyes':
          case 'whiskers':
          case 'mouth':
            return name.includes(selectedSub)
        }
        return !name.match(allBodyTypes)
    }
    return name.includes(selectedSub)
  })

  return filteredNfts.map((nft) => {
    switch (selectedTrait) {
      case 'body':
        if (nft.name === 'skeleton-kitty-body') return nft
        const attribute = nft.name
        const name = attribute
          .replace(subLayerBodyTypes, '')
          .replace('-', ' ')
          .trim()
          .replace(' ', '-')

        const newNft = { ...nft }
        newNft.name = name
        return newNft

      case 'clothes':
      case 'accessory':
        if (selectedSub === 'egyptian') {
          const attribute = nft.name
          const name = attribute.replace('egyptian-', '')

          const newNft = { ...nft }
          newNft.name = name

          return newNft
        }
        if (!nft.name.includes('skeleton-kitty')) return nft
        break
      case 'eyes':
        if (nft.name === 'grumpy-eyes-animated') {
          const newNft = { ...nft }
          newNft.name = 'Grumpy'

          return newNft
        }
        if (nft.name === 'sage-eyes') {
          const newNft = { ...nft }
          newNft.name = 'Sage'

          return newNft
        }
        break
      case 'mouth':
        if (nft.name === 'grumpy-mouth') return nft
        break
    }
    const attribute = nft.name
    if (attribute) {
      const name = attribute
        .replace(allBodyTypes, '')
        .replace('-', ' ')
        .trim()
        .replace(' ', '-')

      const newNft = { ...nft }
      newNft.name = name
      return newNft
    }
  })
}

const nameToLowerCaseWithDashes = (name: string) => {
  return name
    .replace('Uno', '1')
    .replace('Dos', '2')
    .toLowerCase()
    .trim()
    .replaceAll(' ', '-')
}

//Function for displaying the correct avatar with every filter item
const getDisplayAvatar = (
  item: string,
  trait: TraitType,
  avatars: Map<string, string>,
) => {
  const eyesBodyType = /\b(?:normal|skeleton-kitty)\b/
  const attributeName = nameToLowerCaseWithDashes(item)
  const traitName = nameToLowerCaseWithDashes(trait) as TraitType

  const keys = Array.from(avatars.keys())

  const key = keys.find((key) => {
    switch (traitName) {
      case 'body':
      case 'ears':
        if (attributeName.match(subLayerBodyTypes)) {
          return key.includes(attributeName) && key.includes(traitName)
        }
        return (
          !key.match(subLayerBodyTypes) &&
          key.includes(attributeName) &&
          key.includes(traitName)
        )
      case 'head-accessory':
      case 'headgear':
      case 'clothes':
        if (attributeName.includes('trainer-hat')) {
          if (attributeName.includes('skeleton-kitty')) {
            return key.includes('skeleton-kitty') && key.includes('trainer-cap')
          } else {
            return (
              !key.includes('skeleton-kitty') && key.includes('trainer-cap')
            )
          }
        }

        if (attributeName.includes('skeleton-kitty')) {
          return (
            key.includes('skeleton-kitty') &&
            key.includes(traitName) &&
            key.includes(attributeName)
          )
        }

        return (
          !key.includes('skeleton-kitty') &&
          key.includes(traitName) &&
          key.includes(attributeName)
        )
      case 'background':
      case 'accessory':
      case 'floating-item':
      case 'eyewear':
      case 'nose':
        return key.includes(traitName) && key.includes(attributeName)
      case 'mouth':
      case 'whiskers':
        return key.includes('normal') && key.includes(attributeName)
      case 'eyes':
        switch (attributeName) {
          case 'regular':
          case 'normal':
            return key === 'normal-regular-eyes-animated'
          case 'sage':
            return key === 'sage-eyes'
          case 'grumpy':
            return key === 'grumpy-eyes-animated'
          default:
            return (
              key.match(eyesBodyType) && key.includes(attributeName + '-eyes')
            )
        }
    }
  })

  return <FilterAvatar src={avatars.get(key)} name={item} />
}

export const getFilterItems = (
  attribute: { name: keyof TraitTypes; items: string[] },
  avatars: Map<string, string>,
) => {
  return attribute.items
    .filter((item) =>
      Array.from(avatars.keys()).find((key) =>
        key.includes(nameToLowerCaseWithDashes(item)),
      ),
    )
    .map((item) => ({
      name: getDisplayAvatar(item, attribute.name, avatars),
      value: item,
    }))
}
